import { Drawer as MuiDrawer } from '@mui/material';
import * as React from 'react';

export interface DrawerContextType {
  openDrawer: (content: React.ReactNode, onClose?: () => void) => void;
  closeDrawer: () => void;
}

export const DrawerContext = React.createContext<DrawerContextType | undefined>(undefined);

export const DrawerProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [drawerContent, setDrawerContent] = React.useState<React.ReactNode>(null);
  const onCloseCallbackRef = React.useRef<(() => void) | undefined>(undefined);

  const openDrawer = React.useCallback((content: React.ReactNode, onClose?: () => void) => {
    onCloseCallbackRef.current = onClose;
    setDrawerContent(content);
    setIsOpen(true);
  }, []);

  const closeDrawer = React.useCallback(() => {
    if (onCloseCallbackRef.current) {
      onCloseCallbackRef.current();
      onCloseCallbackRef.current = undefined;
    }
    setIsOpen(false);
  }, []);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setDrawerContent(null);
    }
  };

  return (
    <DrawerContext.Provider value={{ openDrawer, closeDrawer }}>
      {children}
      <MuiDrawer
        anchor="right"
        open={isOpen}
        onClose={closeDrawer}
        sx={{ width: { xs: '100%', sm: 480 } }}
        slotProps={{ transition: { onExited: handleTransitionEnd } }}
      >
        {drawerContent}
      </MuiDrawer>
    </DrawerContext.Provider>
  );
};

export default DrawerProvider;
